import type { TCoreButtonStyleProps } from '@core/widgets';
import { SemanticColor, semanticColors } from '../../../theme/colors';
import { TButtonOption } from '../../../types/options';
import { isMobileByTouch } from '../../../utils/helpers';
import { TButtonPillViewProps } from '../ButtonPillView';

type THierarchy = NonNullable<TButtonPillViewProps['hierarchy']>;

export const getPaddingPostfix = (hasIcon: boolean, iconPosition?: TButtonOption['iconPosition'], size?: TButtonOption['size']): string => {
    const prefix = size ? size.charAt(0).toUpperCase() + size.slice(1) : 'Medium';
    const postfix = hasIcon ? (iconPosition === 'right' ? 'IconRight' : 'IconLeft') : '';

    return prefix + postfix;
};

type TSize = NonNullable<TButtonPillViewProps['size']>;

/** Pill box height in px per size — shared by the default and the glass render paths. */
export const PILL_HEIGHT_MAP: Record<TSize, number> = {
    large: 56,
    medium: 40,
    small: 28,
};

/** Label typography per size — shared by the default and the glass render paths. */
export const PILL_FONT_STYLE_MAP: Record<TSize, NonNullable<TCoreButtonStyleProps['fontStyle']>> = {
    large: { fontSize: 17, lineHeight: 24 },
    medium: { fontSize: 17, lineHeight: 24 },
    small: { fontSize: 14, lineHeight: 20 },
};

export const HIERARCHY_BG_MAP: Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'onMain' | 'outline' | 'hovered' | 'disabled' | 'pressed'>> = {
    primary: {
        main: semanticColors[SemanticColor.FillPrimary],
        onMain: semanticColors[SemanticColor.TextPrimaryInverted],
        outline: semanticColors[SemanticColor.BorderEmphasised],
        disabled: {
            main: semanticColors[SemanticColor.FillPrimaryDisabled],
            onMain: semanticColors[SemanticColor.TextPrimaryInvertedDisabled],
        },
        hovered: {
            main: semanticColors[SemanticColor.FillPrimaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillPrimaryPressed],
        },
    },
    primaryBlack: {
        main: 'var(--black-100)',
        onMain: 'var(--white-100)',
        outline: 'var(--white-100)',
        disabled: {
            main: 'var(--white-88-lc)',
            onMain: 'var(--white-100)',
        },
        hovered: {
            main: 'var(--gray-cold-16-lc)',
        },
        pressed: {
            main: 'var(--gray-cold-8-lc)',
        },
    },
    secondary: {
        main: semanticColors[SemanticColor.FillSecondary],
        onMain: semanticColors[SemanticColor.TextEmphasised],
        outline: semanticColors[SemanticColor.BorderEmphasisedInvariable],
        disabled: {
            main: semanticColors[SemanticColor.FillSecondaryDisabled],
            onMain: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
        hovered: {
            main: semanticColors[SemanticColor.FillSecondaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillSecondaryPressed],
        },
    },
    secondaryWhite: {
        main: 'var(--white-100)',
        onMain: 'var(--black-100)',
        outline: 'var(--white-100)',
        disabled: {
            main: 'var(--gray-warm-16-lc)',
            onMain: 'var(--gray-warm-40-lc)',
        },
        hovered: {
            main: 'var(--gray-warm-16-lc)',
        },
        pressed: {
            main: 'var(--gray-warm-8-lc)',
        },
    },
    tertiary: {
        main: semanticColors[SemanticColor.FillTertiary],
        onMain: semanticColors[SemanticColor.TextPrimary],
        disabled: {
            main: semanticColors[SemanticColor.FillTertiaryDisabled],
            onMain: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
        hovered: {
            main: semanticColors[SemanticColor.FillTertiaryHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillTertiaryPressed],
        },
    },
    tertiaryWhite: {
        main: 'var(--white-100)',
        onMain: 'var(--black-100)',
        disabled: {
            main: 'var(--gray-warm-16-lc)',
            onMain: 'var(--gray-warm-40-lc)',
        },
        hovered: {
            main: 'var(--gray-warm-16-lc)',
        },
        pressed: {
            main: 'var(--gray-warm-8-lc)',
        },
    },
    destructive: {
        main: semanticColors[SemanticColor.FillDestructive],
        onMain: semanticColors[SemanticColor.TextError],
        disabled: {
            main: semanticColors[SemanticColor.FillDestructiveDisabled],
            onMain: semanticColors[SemanticColor.TextErrorDisabled],
        },
        hovered: {
            main: semanticColors[SemanticColor.FillDestructiveHovered],
        },
        pressed: {
            main: semanticColors[SemanticColor.FillDestructivePressed],
        },
    },
    highlighted: {},
    tertiaryGlass: {},
};

export const HIERARCHY_TEXT_MAP: Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'hovered' | 'disabled' | 'pressed'>> = {
    primary: {
        main: semanticColors[SemanticColor.TextPrimaryInverted],
        disabled: {
            main: semanticColors[SemanticColor.TextPrimaryInvertedDisabled],
        },
    },
    primaryBlack: {
        main: 'var(--white-100)',
        disabled: {
            main: 'var(--white-100)',
        },
    },
    secondary: {
        main: semanticColors[SemanticColor.TextEmphasised],
        disabled: {
            main: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
    },
    secondaryWhite: {
        main: 'var(--black-100)',
        disabled: {
            main: 'var(--gray-warm-40-lc)',
        },
    },
    tertiary: {
        main: semanticColors[SemanticColor.TextPrimary],
        disabled: {
            main: semanticColors[SemanticColor.TextPrimaryDisabled],
        },
    },
    tertiaryWhite: {
        main: 'var(--black-100)',
        disabled: {
            main: 'var(--gray-warm-40-lc)',
        },
    },
    destructive: {
        main: semanticColors[SemanticColor.TextError],
        disabled: {
            main: semanticColors[SemanticColor.TextErrorDisabled],
        },
    },
    highlighted: {},
    tertiaryGlass: {},
};

export const PADDING_MAP: Record<string, { top: number; right: number; bottom: number; left: number }> = {
    Large: { top: 10, right: 32, bottom: 10, left: 32 },
    LargeIconRight: { top: 10, right: 16, bottom: 10, left: 32 },
    LargeIconLeft: { top: 10, right: 32, bottom: 10, left: 16 },
    Medium: { top: 10, right: 24, bottom: 10, left: 24 },
    MediumIconRight: { top: 10, right: 16, bottom: 10, left: 16 },
    MediumIconLeft: { top: 10, right: 16, bottom: 10, left: 16 },
    Small: { top: 10, right: 16, bottom: 10, left: 16 },
    SmallIconRight: { top: 10, right: 8, bottom: 10, left: 16 },
    SmallIconLeft: { top: 10, right: 16, bottom: 10, left: 8 },
};

export const getMaxWidth = (maxWidth: TButtonPillViewProps['maxWidth']) =>
    maxWidth !== undefined ? maxWidth : isMobileByTouch() ? undefined : 'calc(((100vw - 168px) / 6) * 2 + 24px)';
